import { HeaderActionButton } from "frontend/app-header/header-action-button/header-action-button";
import { useUndoRedoControls } from "frontend/hooks/undo-redo/undo-redo.context";
import useAbility from "frontend/hooks/use-ability";
import { useStateValue } from "frontend/state";
import { useBoardValue } from "frontend/state/board-state";
import { Tooltip } from "frontend/ui-components/floaters/tooltip";
import { useHover } from "frontend/ui-components/floaters/use-hover";
import { useSetAtom } from "jotai";
import { useCallback, useRef } from "react";
import { InstanceType } from "shared/datamodel/schemas/ability";
import { showRestrictedModalAtom } from "state-atoms/general-atoms";
import styles from "./left-header.module.css";

const GRAY_COLOR = "#AFB3BF";
const BLACK_COLOR = "#000";

const UndoSVG = ({ strokeColor }: { strokeColor: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="12" fill="none" viewBox="0 0 14 12">
    <path stroke={strokeColor} strokeLinecap="round" strokeLinejoin="round" d="M4.022 7.786.65 4.4l3.372-3.385"></path>
    <path
      stroke={strokeColor}
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M.65 4.4h9.274c.894 0 1.752.357 2.384.992a3.39 3.39 0 0 1 0 4.787 3.37 3.37 0 0 1-2.384.992H9.08"
    ></path>
  </svg>
);

const RedoSVG = ({ strokeColor }: { strokeColor: string }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="12" fill="none" viewBox="0 0 14 12">
    <path
      stroke={strokeColor}
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M10.127 7.786 13.499 4.4l-3.372-3.385"
    ></path>
    <path
      stroke={strokeColor}
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M13.499 4.4H4.226c-.895 0-1.752.357-2.385.992a3.39 3.39 0 0 0 0 4.787c.633.635 1.49.992 2.385.992h.843"
    ></path>
  </svg>
);

const ActionIcon = ({
  canPerform,
  onClick,
  children,
  name,
}: {
  canPerform: boolean;
  name: string;
  onClick: () => void;
  children: (props: { strokeColor: string }) => React.ReactNode;
}) => {
  const strokeColor = canPerform ? BLACK_COLOR : GRAY_COLOR;
  const iconRef = useRef<HTMLDivElement>(null);
  const iconHover = useHover(iconRef);

  return (
    <HeaderActionButton onClick={onClick} className={styles.undoRedoIcon} ref={iconRef}>
      {children({ strokeColor })}
      {iconHover && (
        <Tooltip design="monday" label={name} relativeTo={iconRef} side={"bottom"} customStyle={{ borderRadius: 3 }} />
      )}
    </HeaderActionButton>
  );
};

export const UndoRedo = () => {
  const [{ board }] = useBoardValue();
  const { canUndo, canRedo, executeRedo, executeUndo } = useUndoRedoControls();
  const [{ user }] = useStateValue();

  const { canPerformAnyAction } = useAbility();

  const isRestrictedUser = board && !canPerformAnyAction(user ?? null, board, InstanceType.Board);
  const setShowRestrictedModal = useSetAtom(showRestrictedModalAtom);

  const handleUndo = useCallback(() => {
    if (isRestrictedUser) {
      setShowRestrictedModal(true);
    }
    executeUndo();
  }, [isRestrictedUser, setShowRestrictedModal, executeUndo]);

  const handleRedo = useCallback(() => {
    if (isRestrictedUser) {
      setShowRestrictedModal(true);
    }
    executeRedo();
  }, [isRestrictedUser, setShowRestrictedModal, executeRedo]);

  return (
    <div className={styles.undoRedo}>
      <ActionIcon canPerform={canUndo} onClick={handleUndo} name="Undo">
        {({ strokeColor }) => <UndoSVG strokeColor={strokeColor} />}
      </ActionIcon>
      <ActionIcon canPerform={canRedo} onClick={handleRedo} name="Redo">
        {({ strokeColor }) => <RedoSVG strokeColor={strokeColor} />}
      </ActionIcon>
    </div>
  );
};
